// ============================================================================
// REQUEST TYPES HOOKS - Digital Approval System
// ============================================================================
// React hooks for Request Types API operations with built-in state management

import { useState, useEffect, useCallback, useMemo } from 'react';
import { requestTypeService } from 'api/services';
import type {
  RequestTypeEntity,
  CreateRequestTypeRequest,
  UpdateRequestTypeRequest,
  RequestTypeQueryParameters,
  RequestTypeListResponse,
  PaginationInfo,
  ID,
} from 'api/types';

// ============================================================================
// QUERY HOOKS (Fetching Data)
// ============================================================================

/**
 * Hook to fetch all request types with pagination and filtering
 */
export const useRequestTypes = (params?: RequestTypeQueryParameters) => {
  const [data, setData] = useState<RequestTypeListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequestTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await requestTypeService.getAll(params);
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchRequestTypes();
  }, [fetchRequestTypes]);

  // Transform backend response to normalized pagination info
  const pagination: PaginationInfo | undefined = useMemo(() => {
    if (!data) return undefined;
    return {
      currentPage: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages,
      totalItems: data.totalCount,
      hasNextPage: data.hasNext,
      hasPreviousPage: data.hasPrevious,
    };
  }, [data]);

  return {
    data,
    items: data?.items || [],
    pagination,
    loading,
    error,
    refetch: fetchRequestTypes,
  };
};

/**
 * Hook to fetch all active request types (most useful for forms)
 */
export const useActiveRequestTypes = () => {
  const [data, setData] = useState<RequestTypeEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchActive = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await requestTypeService.getActive();
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActive();
  }, [fetchActive]);

  return {
    requestTypes: data,
    loading,
    error,
    refetch: fetchActive,
  };
};

/**
 * Hook to fetch a single request type by ID
 */
export const useRequestType = (id: ID | null) => {
  const [data, setData] = useState<RequestTypeEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequestType = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await requestTypeService.getById(id);
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRequestType();
  }, [fetchRequestType]);

  return {
    requestType: data,
    loading,
    error,
    refetch: fetchRequestType,
  };
};

/**
 * Hook to fetch request type by code
 */
export const useRequestTypeByCode = (code: string | null) => {
  const [data, setData] = useState<RequestTypeEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequestType = useCallback(async () => {
    if (!code) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await requestTypeService.getByCode(code);
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    fetchRequestType();
  }, [fetchRequestType]);

  return {
    requestType: data,
    loading,
    error,
    refetch: fetchRequestType,
  };
};

/**
 * Hook to fetch request types by workflow template
 */
export const useRequestTypesByWorkflowTemplate = (workflowTemplateId: ID | null) => {
  const [data, setData] = useState<RequestTypeEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequestTypes = useCallback(async () => {
    if (!workflowTemplateId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await requestTypeService.getByWorkflowTemplate(workflowTemplateId);
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [workflowTemplateId]);

  useEffect(() => {
    fetchRequestTypes();
  }, [fetchRequestTypes]);

  return {
    requestTypes: data,
    loading,
    error,
    refetch: fetchRequestTypes,
  };
};

// ============================================================================
// MUTATION HOOKS (Changing Data)
// ============================================================================

/**
 * Hook to create a new request type
 */
export const useCreateRequestType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createRequestType = async (
    data: CreateRequestTypeRequest,
    options?: {
      onSuccess?: (requestType: RequestTypeEntity) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      const response = await requestTypeService.create(data);
      setSuccess(true);
      if (options?.onSuccess) options.onSuccess(response);
      return response;
    } catch (err) {
      const error = err as Error;
      setError(error);
      if (options?.onError) options.onError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createRequestType, loading, error, success };
};

/**
 * Hook to update a request type
 */
export const useUpdateRequestType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateRequestType = async (
    id: ID,
    data: UpdateRequestTypeRequest,
    options?: {
      onSuccess?: (requestType: RequestTypeEntity) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      const response = await requestTypeService.update(id, data);
      setSuccess(true);
      if (options?.onSuccess) options.onSuccess(response);
      return response;
    } catch (err) {
      const error = err as Error;
      setError(error);
      if (options?.onError) options.onError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { updateRequestType, loading, error, success };
};

/**
 * Hook to delete a request type
 */
export const useDeleteRequestType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteRequestType = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      await requestTypeService.delete(id);
      setSuccess(true);
      if (options?.onSuccess) options.onSuccess();
    } catch (err) {
      const error = err as Error;
      setError(error);
      if (options?.onError) options.onError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { deleteRequestType, loading, error, success };
};
